import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { formatDuration } from "../ui/Timer.js";
import { arenaApi, expeditionsApi, unitsApi } from "../units/api.js";
import { useUnits } from "../units/state.js";
import { unitName } from "../units/shared.js";
import { useReducedMotion } from "../units/useReducedMotion.js";
import type { BattleReport } from "../units/types.js";
import { worldApi } from "../world/api.js";
import type { RepairPreview } from "./reportTypes.js";
import "./battle-results.css";

export function GrowthPanel({ report }: { report: BattleReport | null }) {
  const t = useT(),
    reduced = useReducedMotion();
  const [arrived, setArrived] = useState(reduced);
  useEffect(() => {
    if (reduced) {
      setArrived(true);
      return;
    }
    setArrived(false);
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setArrived(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [report?.id, reduced]);
  const growth = report?.details?.growth;
  if (!growth?.length) return null;
  return (
    <section className={`battle-growth${reduced ? " battle-growth-static" : ""}`} data-testid="result-growth">
      <h3>{t("battle.growth")}</h3><p className="battle-loss-summary" data-testid="result-loss-summary">{growth.filter(u=>u.hpAfter<100).length} {pick({de:"beschädigt",en:"damaged"})} · {growth.filter(u=>u.hpAfter===0).length} {pick({de:"Wracks",en:"wrecks"})}</p>
      <div className="battle-growth-grid">
        {growth.map((unit) => {
          const capped = unit.toLevel >= unit.levelCap;
          const before =
            unit.fromLevel >= unit.levelCap
              ? 100
              : (unit.fromXp / Math.max(1, unit.fromXp + unit.fromXpToNext)) * 100;
          const after = capped ? 100 : (unit.toXp / Math.max(1, unit.toXp + unit.toXpToNext)) * 100;
          return (
            <div className="battle-growth-unit" key={unit.rowId}>
              <div className="row">
                <GameIcon id={unit.unitId} size={56}/><strong>{unitName(unit.unitId)}</strong>
                <span className="grow" />
                <span className="mono">
                  Lv {unit.fromLevel} <FunctionalIcon name="arrowRight" /> {unit.toLevel}
                </span>
              </div>
              <div
                className="battle-growth-track"
                role="progressbar"
                aria-label={`${unitName(unit.unitId)} XP`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(after)}
              >
                <span style={{ width: `${arrived ? after : before}%` }} />
              </div>
              <div className="battle-growth-caption">
                <span>+{formatCompact(unit.appliedXp)} XP</span>
                <span>{t("battle.wear", { before: unit.hpBefore, after: unit.hpAfter })}</span>
              </div>
              {capped ? (
                <p className="hint">{t("battle.xpStored", { n: formatCompact(unit.toXp) })}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** This quote is live account state; historical report damage never substitutes for queue availability. */
export function RepairPanel({ battleId }: { battleId: string }) {
  const t = useT(),
    run = useUnits((state) => state.run);
  const [preview, setPreview] = useState<RepairPreview | null>(null);
  const [failed, setFailed] = useState(false),
    [pending, setPending] = useState(false);
  const [started, setStarted] = useState<number | null>(null);
  const lock = useRef(false),
    alive = useRef(true),
    generation = useRef(0);
  const refresh = async () => {
    const current = ++generation.current;
    try {
      const next = await unitsApi.repairPreview();
      if (alive.current && current === generation.current) {
        setPreview(next);
        setFailed(false);
      }
    } catch {
      if (alive.current && current === generation.current) setFailed(true);
    }
  };
  useEffect(() => {
    alive.current = true;
    setPreview(null);
    setStarted(null);
    void refresh();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, [battleId]);
  const queue = async () => {
    if (lock.current || (!preview?.canStart || !preview.canAfford)) return;
    lock.current = true;
    setPending(true);
    try {
      const result = await run(() => unitsApi.repairAll());
      if (alive.current && result) setStarted(result.started);
      await refresh();
    } finally {
      lock.current = false;
      if (alive.current) setPending(false);
    }
  };
  const queued = preview?.units.filter((unit) => unit.status === "queued").length ?? 0;
  const deployed = preview?.units.filter((unit) => unit.status === "deployed").length ?? 0;
  return (
    <section className="battle-repair" data-testid="result-repair-preview">
      <h3>{t("battle.repair.heading")}</h3>
      {failed ? (
        <p role="alert">{t("battle.repair.unavailable")}</p>
      ) : preview ? (
        <>
          <p>{t("battle.repair.free", { used: preview.used, slots: preview.slots })}</p>
          <p data-testid="repair-batch-cost">{preview.costResources.ALY} ALY · {preview.costResources.POL} POL{!preview.canAfford ? " · "+pick({de:"Zu wenig Material im Lager",en:"Not enough materials in storage"}) : ""}</p>
          <p className="hint">{t("battle.repair.counts", { ready: preview.canStart, queued, deployed })}</p>
          {preview.canStart ? (
            <p className="hint">
              {t("battle.repair.duration", { time: formatDuration(preview.nextBatchMs) })}
            </p>
          ) : null}
          <details>
            <summary>{t("battle.repair.units", { n: preview.units.length })}</summary>
            {preview.units.map((unit) => (
              <div className="battle-repair-row" key={unit.rowId}>
                <span>{unitName(unit.unitId)}</span>
                <span>
                  {t(`battle.repair.${unit.status}`)} · {formatDuration(unit.repairMs)}
                </span>
              </div>
            ))}
          </details>
        </>
      ) : (
        <p className="hint">{t("common.loading")}</p>
      )}
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button
          onClick={() => void queue()}
          loading={pending}
          disabled={!preview?.canStart || failed}
          data-testid="result-repair-all"
        >
          {t("battle.repair.queue", { n: preview?.canStart ?? 0 })}
        </Button>
        <Button variant="ghost" onClick={() => void refresh()} disabled={pending}>
          {t("battle.repair.refresh")}
        </Button>
      </div>
      {started !== null ? <p role="status">{t("battle.repair.started", { n: started })}</p> : null}
    </section>
  );
}

export function RepeatBattleButton({
  report,
  onClose,
}: {
  report: BattleReport | null;
  onClose: () => void;
}) {
  const t = useT(),
    account = useSession((state) => state.account);
  const run = useUnits((state) => state.run),
    setResult = useUnits((state) => state.setResult);
  const [pending, setPending] = useState(false),
    lock = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const target = report?.details?.target;
  if (!report || report.accountId !== account?.id || !target) return null;
  const repeat = async () => {
    if (lock.current) return;
    lock.current = true;
    setPending(true);
    try {
      if (target.kind === "arena") {
        const next = await run(() => arenaApi.attack(target.opponentId, target.squadSlot));
        if (next && alive.current) {
          onClose();
          setResult({
            outcome: next,
            context: "arena",
            extra: { ratingDelta: next.ratingDelta, rating: next.rating, coins: next.coins },
          });
          void useUnits.getState().refreshArena();
        }
      } else if (target.kind === "expedition") {
        const next = await run(() => expeditionsApi.attack(target.stageId, target.squadSlot));
        if (next && alive.current) {
          onClose();
          setResult({
            outcome: next,
            context: "expedition",
            extra: { stars: next.stars, firstClear: next.firstClear },
          });
          void useUnits.getState().refreshExpeditions();
        }
      } else if (target.kind === "nexus") {
        onClose();
        navigate("/app/nexus");
      } else {
        const next = await run(() =>
          worldApi.march({
            x: target.x,
            y: target.y,
            action: target.action,
            squadSlot: target.squadSlot,
            ...(target.choice ? { choice: target.choice } : {}),
          }),
        );
        if (next && alive.current) {
          onClose();
          navigate("/app/map");
        }
      }
    } finally {
      lock.current = false;
      if (alive.current) setPending(false);
    }
  };
  const destination =
    target.kind === "expedition"
      ? target.stageId
      : target.kind === "world"
        ? `${target.x}, ${target.y}`
        : (report.opponentName ?? t("battle.defender"));
  return (
    <Button
      variant="gold"
      loading={pending}
      onClick={() => void repeat()}
      title={target.kind === "nexus" ? pick({ de: "Einsatz, Truppwechsel und nächste Welle prüfen", en: "Review the run, switch squads and inspect the next wave" }) : t("battle.again.target", { target: destination, squad: target.squadSlot })}
      data-testid="result-attack-again"
    >
      {target.kind === "nexus" ? pick({ de: "Zum NEXUS", en: "NEXUS" }) : t("battle.again")}
    </Button>
  );
}
